import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  DoCheck
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Apollo } from 'apollo-angular';
import {
  auditTime,
  filter,
  map,
  takeUntil,
  tap,
  first,
  withLatestFrom
} from 'rxjs/operators';
import { EventMetadata, EventService, EventState, StreamSettings } from '@app/event/event.service';
import { Observable, Subject } from 'rxjs';

import { Event } from '@app/graphql/generated';
import { ModalService } from '@app/shared/modal/modal.service';
import { UserService } from '@app/user/user.service';
import { SettingsComponent } from '@app/stream/settings/settings/settings.component';
import { ResponsiveService } from '@app/utility/responsive.service';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { UntilDestroy } from '@ngneat/until-destroy';
import { config } from '@app/app.config';
import { AuthService } from '@app/auth/auth.service';
import { StreamClient } from '@app/stream/stream.client';
import { speechLanguages } from '@app/translateLanguages';

@UntilDestroy()
@Component({
  selector: 'app-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss']
})
export class EventComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  event: Event;
  paragraphs = [];
  durationInMinutes = 0;
  waypoints = new Array(6);
  scrolledToStart = false;
  destroy = new Subject<any>();
  scrolledToEnd = false;
  @HostBinding('class.isScrolling') isScrollingDelay = false;
  isScrolling: Observable<boolean>;
  mouseY = 0;
  mouseYY;
  firstTimeStampInSeconds: number;
  lastTimeStampInSeconds: number;
  scrollPercentage: number = 0.0;
  isMouseClicked = new Subject<boolean>();
  sampleTimes = [];
  scrollTime = '';
  timeAtScrollLocation: string;
  @Input() window: number;
  @Input() multiWindow: boolean;
  @Input() isCurrentlyStreaming: boolean;
  hasStenoPermission: boolean;
  autoScroll = true;
  language: string = null;
  @ViewChild('streamBox') private streamBox: ElementRef;
  @ViewChild('contentBox') private contentBox: ElementRef;
  @ViewChild('scrollBar') private scrollBar: ElementRef;
  @ViewChildren('words') private words: QueryList<any>;
  private isProgrammaticallyScrolling: boolean = false;
  private isNearBottom: boolean;
  // private scrollContainer: any;
  private scrollBarScrollY: Observable<any>;
  private scrollBarContainer: any;
  private initialScrollBottom = false;
  private currentUser: any;
  private lastStreamScrollTop: number = 0;
  private canEdit: boolean = false;
  private eventWords: string[];
  private streamClient: StreamClient;
  private eventStatusInterval: ReturnType<typeof setInterval>;
  private isCheckingStatus: boolean;
  eventMetadata: EventMetadata;

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
    public eventService: EventService,
    private appRef: ApplicationRef,
    // private modalService: NgbModal,
    private appModalService: ModalService,
    private userService: UserService,
    private responsiveService: ResponsiveService,
    private authService: AuthService
  ) {
  }

  async ngOnInit() {
    this.userService
      .currentUser()
      .pipe(
        untilDestroyed(this),
        tap(user => {
          this.currentUser = user;
          this.hasStenoPermission = user.permissions.some(
            permission => permission.name === 'steno'
          );
        })
      )
      .subscribe();

    this.streamClient = new StreamClient(`${config.httpURL.replace('http', 'ws').replace('https', 'ws')}/api/stream`, this.authService.getAccessToken());

    this.eventService.resubscribeHandler = this.resubscribe.bind(this);
    this.eventService.unsubscribeHandler = this.unsubscribe.bind(this);
    
    console.log(`STREAM: ngOnInit()`);
    
    const eventId = this.route.snapshot.params.eventId;
    const language = this.route.snapshot.queryParams.lang;
    
    await this.setupStream(eventId, language ?? 'en-US');

    await this.getEventMetadata(eventId);
  }

  async resubscribe() {
    await this.unsubscribe();

    const eventId = this.route.snapshot.params.eventId;
    const language = this.route.snapshot.queryParams.lang;
    
    await this.setupStream(eventId, language ?? 'en-US');
  }

  async getEventMetadata(eventId: string) {
    this.eventService.getEventMetadata(eventId).subscribe(metadata => {
      if (metadata.profile) {
        const profileSettings: Partial<StreamSettings> = {
          font: metadata.profile.font,
          fontSize: metadata.profile.fontSize,
          background: metadata.profile.background,
          textColor: metadata.profile.textColor
        };
        const appliedSettings = this.eventService.setInitialSettings(profileSettings, { window: 1 });
        localStorage.setItem(`stream-settings-${eventId}`, JSON.stringify(appliedSettings));
        sessionStorage.setItem(`stream-settings-${eventId}`, JSON.stringify(appliedSettings));
      }

      metadata.language = speechLanguages.find(l => l.value === metadata.language)?.label;
      this.eventMetadata = metadata;
    });
  }

  async setupStream(eventId: string, language: string) {
    this.streamClient.appendText = this.appendText.bind(this);
    this.streamClient.writeText = this.writeText.bind(this);
    // pull existing text
    await this.streamClient.pull(eventId, language);
    // set up event status timer
    this.eventStatusInterval = setInterval(() => {

      if (this.isCheckingStatus)
        return;

      this.isCheckingStatus = true;

       const status$ = this.eventService.getEventStatus(eventId)
        .pipe(first())
        .subscribe({
        next: (status: EventState) => {
          if (status.completed) {
            clearInterval(this.eventStatusInterval);
            this.unsubscribe();
          }
        },
        error: (err) => {
          console.error('Error checking event status:', err);
        },
        complete: () => {
            this.isCheckingStatus = false;
        }
      });

    }, 5000);
    
    // set up subscription
    this.streamClient.subscribe(eventId, language);
  }

  async unsubscribe() {
    await this.streamClient.unsubscribe();  
  }

  private writeText(text: string) {
    this.paragraphs = [];

    this.appendText(text);
  }

  private appendText(text: string) {
    const preferredMaxChars = 40;
    const maxChars = 80;
    let curChars = 0;

    const paragraphs: string[][] = this.paragraphs ?? [];

    const rawLines = text.split('\n');

    for (const rawLine of rawLines) {
      const words = rawLine.trim().split(/\s+/);
      let currentParagraph = paragraphs[paragraphs.length - 1];

      if (!currentParagraph) {
        currentParagraph = [];
        paragraphs.push(currentParagraph);
      }

      for (const word of words) {
        const isPunctuation = /[.!?]$/.test(word);

        currentParagraph.push(word);
        curChars += word.length;

        if ((isPunctuation && curChars >= preferredMaxChars) || curChars >= maxChars) {
          curChars = 0;
          currentParagraph = [];
          paragraphs.push(currentParagraph);
        }
      }

      curChars = 0;
      currentParagraph = [];
      paragraphs.push(currentParagraph);
    }

    if (paragraphs.length && paragraphs[paragraphs.length - 1].length === 0) {
      paragraphs.pop();
    }

    this.updateParagraphs(paragraphs);
  }

  updateParagraphs(paragraphs) {
    this.paragraphs = paragraphs;
    setTimeout(() => {
      if (!this.canScrollToBottom()) {
        return;
      }
      this.scrollToBottom();
    }, 0);
  }

  scrollToBottom({ smooth } = { smooth: true }): void {
    if (!this.streamBox?.nativeElement) return;
    this.streamBox.nativeElement.scrollTop = this.streamBox.nativeElement.scrollHeight;
  }

  unixTimestampToHHMM(timestamp: number) {
    try {
      return new Date(timestamp * 1000).toISOString().substr(12, 4);
    } catch (err) {
      return '0:00';
    }
  }

  ngOnDestroy(): void {
    console.log(`STREAM: ngOnDestroy()`);
    this.destroy.next();
    this.destroy.complete();
  }

  // getTimeAtScrollLocation() {
  //   // const scrollPercentage = this.getScrollPercentage();
  //   const diffInSeconds =
  //     this.lastTimeStampInSeconds - this.firstTimeStampInSeconds;
  //   const timeAtScrollLocation = this.unixTimestampToHHMM(
  //     diffInSeconds * scrollPercentage
  //   );
  //   return timeAtScrollLocation;
  // }

  ngAfterViewInit(): void {
  }

  canScrollToBottom() {
    return this.autoScroll;
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(`STREAM: ngOnChanges(): ${JSON.stringify(changes)}`);
    this.responsiveService.detectChanges();
    // ;
  }

  sendSteno(words: string) {
    console.log(`STREAM: sendSteno(): ${words}`);
    this.eventService
      .appendText(
        this.route.snapshot.params.eventId,
        words
          .split(' ')
          .map(word => ({ word, startTime: new Date().toISOString() }))
      )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  openStreamSettings() {
    // BootStrap
    // this.modalService.dismissAll();
    // const modalRef = this.modalService.open(SettingsComponent, {
    //   backdrop: false,
    //   windowClass: 'stream-settings'
    // });
    // const instance = modalRef.componentInstance;
    // instance.window = this.window;
    // instance.event = this.event;
    // instance.language = this.language;
    // instance.settings$ =
    //   this.window === 1
    //     ? this.eventService.settings1$
    //     : this.eventService.settings2$;
  }

  streamBoxScrolled($event: Event) {
    const currentScrollTop = this.streamBox.nativeElement.scrollTop;
    const scrollHeight = this.streamBox.nativeElement.scrollHeight;
    const clientHeight = this.streamBox.nativeElement.clientHeight;
    const scrollDifference = currentScrollTop - this.lastStreamScrollTop;
    const scrollHeightTopDifference = scrollHeight - currentScrollTop;
    const hasScrolledUp = Math.sign(scrollDifference) === -1;

    if (hasScrolledUp) {
      this.autoScroll = false;
    }

    if (!hasScrolledUp && scrollHeightTopDifference - clientHeight < 20) {
      this.autoScroll = true;
    }

    this.lastStreamScrollTop = currentScrollTop;
  }
}
